import { useState, useRef, useEffect, useCallback } from "react";

/* ── Proximity Mask Effect — hidden data grid revealed by cursor ── */
function PixelGrid() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const mouseRef = useRef({ x: -1000, y: -1000 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const handleLeave = () => { mouseRef.current = { x: -1000, y: -1000 }; };

    window.addEventListener("mousemove", handleMove);
    canvas.addEventListener("mouseleave", handleLeave);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      canvas.removeEventListener("mouseleave", handleLeave);
    };
  }, []);

  const draw = useCallback((time: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = 2;
    const w = canvas.offsetWidth;
    const h = canvas.offsetHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);

    const t = time * 0.0002;
    const mx = mouseRef.current.x;
    const my = mouseRef.current.y;
    const revealRadius = 250;

    const gap = 24;
    const cols = Math.ceil(w / gap);
    const rows = Math.ceil(h / gap);

    // Data-like characters that appear in the grid
    const chars = ["0", "1", "·", "+", "—", "×", "%", "∅", "Δ", "μ", "σ", "→"];

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = c * gap + gap / 2;
        const y = r * gap + gap / 2;

        // Distance from cursor
        const dx = x - mx;
        const dy = y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);

        // Proximity factor — smooth Gaussian-like falloff, no hard edge
        const proximity = Math.exp(-(dist * dist) / (revealRadius * revealRadius * 0.3));

        if (proximity <= 0.02) continue;

        // Combined alpha: very soft fade
        const alpha = proximity * proximity * 0.6;

        // Size grows near cursor
        const size = 3 + proximity * 5;

        // Color shifts slightly based on position
        const blue = Math.floor(30 + proximity * 30);
        const green = Math.floor(58 + proximity * 70);
        const blueC = Math.floor(110 + proximity * 90);

        // Show data characters near cursor
        const charIdx = (r * 17 + c * 13 + Math.floor(t * 2)) % chars.length;
        ctx.font = `${8 + proximity * 4}px 'IBM Plex Mono', monospace`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = `rgba(${blue},${green},${blueC},${alpha})`;
        ctx.fillText(chars[charIdx], x, y);
      }
    }

    animRef.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 0, pointerEvents: "none" }}
    />
  );
}

/* ── Typing hero text ── */
const HERO_PHRASES = ["model-ready features.", "engineered variables.", "predictive signals.", "structured priors."];
const HERO_COLORS = ["#1e3a6e", "#3b7dd8", "#6366f1", "#8b5cf6"];

function HeroTyper() {
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const phrase = HERO_PHRASES[phraseIdx];

    if (!deleting && charIdx < phrase.length) {
      // Typing
      const timer = setTimeout(() => setCharIdx(c => c + 1), 70 + Math.random() * 40);
      return () => clearTimeout(timer);
    }
    if (!deleting && charIdx === phrase.length) {
      // Pause at end
      const timer = setTimeout(() => setDeleting(true), 2000);
      return () => clearTimeout(timer);
    }
    if (deleting && charIdx > 0) {
      // Deleting
      const timer = setTimeout(() => setCharIdx(c => c - 1), 35);
      return () => clearTimeout(timer);
    }
    if (deleting && charIdx === 0) {
      // Move to next phrase
      setDeleting(false);
      setPhraseIdx(i => (i + 1) % HERO_PHRASES.length);
    }
  }, [charIdx, deleting, phraseIdx]);

  const text = HERO_PHRASES[phraseIdx].slice(0, charIdx);
  const color = HERO_COLORS[phraseIdx];

  return (
    <span style={{
      fontStyle: "italic", fontWeight: 700, fontFamily: "'Merriweather', serif",
      color, transition: "color 0.5s ease",
    }}>
      {text}
      <span style={{
        display: "inline-block", width: 2, height: "0.85em", background: color,
        marginLeft: 2, verticalAlign: "baseline",
        animation: "cursorBlink 0.8s step-end infinite",
        transition: "background 0.5s ease",
      }} />
    </span>
  );
}

/* ── Scroll reveal hook ── */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.scroll-reveal');
    if (!els.length) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
        }
      });
    }, { threshold: 0.1, rootMargin: "0px 0px -40px 0px" });
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

/* ── Morphing Grid Background ── */
function MorphGrid() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const draw = useCallback((time: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const w = canvas.width = canvas.offsetWidth * 2;
    const h = canvas.height = canvas.offsetHeight * 2;
    ctx.clearRect(0, 0, w, h);

    const cols = 18;
    const rows = 14;
    const cellW = w / (cols - 1);
    const cellH = h / (rows - 1);
    const t = time * 0.0004;

    // Build warped grid points
    const pts: { x: number; y: number }[][] = [];
    for (let r = 0; r < rows; r++) {
      pts[r] = [];
      for (let c = 0; c < cols; c++) {
        const baseX = c * cellW;
        const baseY = r * cellH;
        const warpX = Math.sin(t + r * 0.4 + c * 0.2) * 18 + Math.cos(t * 0.7 + c * 0.3) * 12;
        const warpY = Math.cos(t * 0.8 + c * 0.3 + r * 0.2) * 15 + Math.sin(t * 0.5 + r * 0.4) * 10;
        pts[r][c] = { x: baseX + warpX, y: baseY + warpY };
      }
    }

    // Draw horizontal lines
    for (let r = 0; r < rows; r++) {
      const alpha = 0.08 + 0.04 * Math.sin(t + r * 0.5);
      ctx.strokeStyle = `rgba(30,58,110,${alpha})`;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(pts[r][0].x, pts[r][0].y);
      for (let c = 1; c < cols; c++) {
        const prev = pts[r][c - 1];
        const curr = pts[r][c];
        const cpx = (prev.x + curr.x) / 2;
        const cpy = (prev.y + curr.y) / 2;
        ctx.quadraticCurveTo(prev.x, prev.y, cpx, cpy);
      }
      ctx.stroke();
    }

    // Draw vertical lines
    for (let c = 0; c < cols; c++) {
      const alpha = 0.06 + 0.03 * Math.cos(t * 0.9 + c * 0.4);
      ctx.strokeStyle = `rgba(59,125,216,${alpha})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(pts[0][c].x, pts[0][c].y);
      for (let r = 1; r < rows; r++) {
        const prev = pts[r - 1][c];
        const curr = pts[r][c];
        const cpx = (prev.x + curr.x) / 2;
        const cpy = (prev.y + curr.y) / 2;
        ctx.quadraticCurveTo(prev.x, prev.y, cpx, cpy);
      }
      ctx.stroke();
    }

    // Subtle glow dots at intersections near center
    const cx = w / 2, cy = h / 2;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const p = pts[r][c];
        const dist = Math.sqrt((p.x - cx) ** 2 + (p.y - cy) ** 2);
        const maxDist = Math.sqrt(cx * cx + cy * cy);
        const proximity = 1 - dist / maxDist;
        const dotAlpha = proximity * 0.12 * (0.6 + 0.4 * Math.sin(t * 1.2 + r + c));
        if (dotAlpha > 0.02) {
          ctx.fillStyle = `rgba(99,102,241,${dotAlpha})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    animRef.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
}

interface Props {
  onLogin: () => void;
}

const USE_CASES = [
  {
    tag: "Data Analyst · Fortune 500",
    tagColor: "#2563eb",
    title: "The churn model was missing the one variable that mattered",
    scenario: "A data analyst at a large telecom is building a customer churn model. The dataset has 40 features — usage, billing, tenure, demographics. The model works okay (AUC 0.74) but leadership wants better. The analyst knows something is missing but doesn't know what.",
    expert: "Tacit matches them with a former VP of Customer Success at a competing carrier who spent 15 years studying churn patterns.",
    quote: "Usage drops don't predict churn. What predicts churn is the customer calling support twice in 30 days and not getting resolution. After the second unresolved call, you have a 72-hour window before they port out. Your model should have a binary: two_unresolved_support_calls_30d.",
    speaker: "Former VP Customer Success, T-Mobile · 15 years",
    extracted: [
      "unresolved_support_2x_30d (binary) — 90% certainty",
      "resolution_time_gt_48h (threshold, interaction with tenure)",
      "port_window_72h (time-to-event, post-trigger)",
    ],
    result: "The analyst had support call counts in their data but was using total volume, not unresolved pairs within 30 days. One feature transformation — already possible with existing data — lifted AUC from 0.74 to 0.81. The expert identified the behavioral trigger the model couldn't find statistically.",
    metric: "AUC 0.74 → 0.81",
    metricLabel: "from one feature transform",
  },
  {
    tag: "Product Data Scientist · Growth Team",
    tagColor: "#059669",
    title: "Why new users were churning before day 7",
    scenario: "A data scientist on a B2B SaaS growth team is modeling early user activation. They have event data — signups, feature usage, invites sent — but can't figure out why 60% of trial users go silent after day 3. The product team suspects it's an onboarding issue but has no data to confirm.",
    expert: "Tacit matches them with a former Head of Growth Analytics at a similar SaaS company who solved the exact same retention curve.",
    quote: "It's not about how many features they try. It's whether they connect their own data source within the first 48 hours. If they're still using sample data on day 3, they've already decided this isn't for them. Track time_to_first_real_data_connection. That's your activation metric.",
    speaker: "Former Head of Growth Analytics, Amplitude · 8 years",
    extracted: [
      "data_connected_48h (binary) — 88% certainty",
      "sample_data_only_day3 (binary, negative signal)",
      "invite_sent_pre_activation (interaction, 2.3x retention lift)",
    ],
    result: "The team had a 'features_used_count' metric for activation. The expert reframed it: it's not breadth of usage, it's whether they brought their own data. time_to_first_real_data_connection was derivable from existing event logs but nobody had thought to create it.",
    metric: "Day-7 retention +18%",
    metricLabel: "after redefining the activation metric",
  },
  {
    tag: "Consulting · Predictive Model for Client",
    tagColor: "#7c3aed",
    title: "Building a store closure model with 3 weeks and no domain knowledge",
    scenario: "A data science team at a consulting firm is helping a national restaurant chain predict which locations will close within 12 months. They have financials, foot traffic, and lease data for 2,000 locations. The model is due in 3 weeks. Nobody on the team has restaurant industry experience.",
    expert: "Tacit matches them with a former SVP of Operations at a major QSR chain who closed 400+ underperforming locations over a decade.",
    quote: "Your financials will show declining revenue, but by then it's too late. The leading indicator is manager turnover. When a GM leaves and the replacement is a lateral transfer — not a promotion — that store is in a death spiral. Track gm_turnover_lateral_replacement as a binary. Also, any store where labor costs cross 38% of revenue for two consecutive quarters — that's the threshold. Not one quarter. Two.",
    speaker: "Former SVP Operations, Yum! Brands · 22 years, 400+ closures managed",
    extracted: [
      "gm_turnover_lateral (binary) — 82% certainty",
      "labor_cost_pct_gt_38_2q (threshold, 2-quarter consecutive)",
      "revenue_decline_rate_vs_market (relative, not absolute)",
      "lease_renewal_18mo_window (interaction with performance)",
    ],
    result: "The consulting team had manager data but was only tracking vacancy duration, not replacement quality. They had labor cost % but weren't applying the 2-quarter consecutive rule. Two feature transforms from a single 45-minute expert call. Model accuracy jumped and the client approved the engagement extension.",
    metric: "4 new features",
    metricLabel: "from 45 minutes with the right expert",
  },
];

const STEPS = [
  { num: "1", title: "Match", desc: "Describe what you're predicting — or upload your dataset. We match you with an expert who's solved this problem before." },
  { num: "2", title: "Connect Data", desc: "Link your database or upload your file. Your data stays connected throughout the session." },
  { num: "3", title: "Interview + Live Scan", desc: "Expert talks naturally. We extract variables and scan your data in real time — showing matches, distributions, and follow-up questions as the conversation happens." },
  { num: "4", title: "Triage + Engineer", desc: "Review direct matches, proxies, derived, and missing variables. Accept or skip. We generate the transformation code in Python or SQL." },
  { num: "5", title: "Push", desc: "New feature columns pushed back to your live table. Feature card documents every addition — what changed, why, and who said it." },
];

/* ── Animated mini-demo — alternates between technical + non-technical expert ── */
const DEMO_SETS = [
  // Set 1: Data scientist (technical)
  [
    { type: "you", text: "We're building a driver churn model. What's everyone missing?", delay: 0 },
    { type: "expert", text: "Surge fatigue. Above 40% surge exposure, drivers start gaming the system — they go offline to trigger surges. Net effect on supply is actually negative. Your model probably treats surge as a positive incentive. Above 40%, it's the opposite.", delay: 2400,
      signal: { name: "surge_fatigue_gt_40", tag: "threshold", certainty: "82%", quote: "Above 40%, drivers aren't responding to demand — they're manufacturing it",
        match: { col: "Surge_Session_Pct", status: "direct", pct: "22%", strength: "Moderate", corr: "+0.19" } } },
    { type: "you", text: "What about new driver onboarding?", delay: 6500 },
    { type: "expert", text: "Eight rides in week one. That's your activation number. Below that, 73% churn within 90 days. They never build the muscle memory. First bad experience and they're gone.", delay: 8500,
      signal: { name: "first_week_lt_8", tag: "threshold", certainty: "88%", quote: "Eight rides in week one. Below that, you've already lost them.",
        match: { col: "First_Week_Rides", status: "direct", pct: "62%", strength: "Strong", corr: "-0.34" } } },
    { type: "you", text: "How should we use ratings?", delay: 12000 },
    { type: "expert", text: "Forget the number. Look at the slope. A 4.9 dropping to 4.7 in two weeks is scarier than a steady 4.75. That's your signal — the 14-day delta, not the level.", delay: 14000,
      signal: { name: "rating_drop_14d", tag: "variable", certainty: "78%", quote: "Look at the slope, not the number",
        match: { col: "current_rating - rating_30d", status: "derived", pct: "7%", strength: "Strong", corr: "-0.27" } } },
  ],
  // Set 2: Farmer (non-technical)
  [
    { type: "you", text: "What do you look at to know if a field will have a good year?", delay: 0 },
    { type: "expert", text: "It all starts with the ground in April. Grab a handful and squeeze. If it crumbles instead of holding together, you're starting behind. Doesn't matter how much rain you get later — the roots set up shallow and never recover.", delay: 2400,
      signal: { name: "apr_moisture_lt_40", tag: "variable", certainty: "92%", quote: "If it crumbles, you're starting behind.",
        match: { col: "Apr_Soil_Moisture", status: "direct", pct: "44%", strength: "Strong", corr: "-0.38" } } },
    { type: "you", text: "What about pests?", delay: 6500 },
    { type: "expert", text: "It's not how many bugs — it's when they show up. Aphids before the Fourth of July? That field's done. After the Fourth, the plants can handle it. I've never seen a field recover from early aphids. Not once in 40 years.", delay: 8500,
      signal: { name: "pest_before_july4", tag: "threshold", certainty: "85%", quote: "Before the Fourth of July, that field's done.",
        match: { col: "First_Pest_Detection_DOY", status: "direct", pct: "38%", strength: "Moderate", corr: "-0.29" } } },
    { type: "you", text: "Does what your neighbors plant matter?", delay: 12000 },
    { type: "expert", text: "When the guy next to you goes corn and you're still doing beans, you're fighting for the same water table. In a dry year, my beans next to his corn do 15-20% worse. Nobody puts this in their models but every farmer knows it.", delay: 14000,
      signal: { name: "neighbor_corn_flag", tag: "variable", certainty: "78%", quote: "You're fighting for the same water.",
        match: { col: "Neighbor_Crop", status: "direct", pct: "58%", strength: "Moderate", corr: "+0.18" } } },
  ],
];

function LiveDemo() {
  const [visibleLines, setVisibleLines] = useState(0);
  const [signals, setSignals] = useState<typeof DEMO_SETS[0][0]["signal"][]>([]);
  const [cycle, setCycle] = useState(0);
  const demoLines = DEMO_SETS[cycle % DEMO_SETS.length];
  const isDataScientist = cycle % 2 === 0;

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    setVisibleLines(0);
    setSignals([]);

    demoLines.forEach((line, i) => {
      timers.push(setTimeout(() => {
        setVisibleLines(i + 1);
        if (line.signal) {
          setTimeout(() => setSignals(prev => [...prev, line.signal!]), 800);
        }
      }, line.delay));
    });

    // Restart after all lines shown
    timers.push(setTimeout(() => setCycle(c => c + 1), 16000));

    return () => timers.forEach(clearTimeout);
  }, [cycle]);

  const tagColors: Record<string, { bg: string; color: string }> = {
    variable: { bg: "rgba(99,102,241,0.1)", color: "#6366f1" },
    threshold: { bg: "rgba(245,158,11,0.1)", color: "#d97706" },
    leading: { bg: "rgba(16,185,129,0.1)", color: "#059669" },
  };

  return (
    <div style={{
      width: "100%", maxWidth: 820, borderRadius: 16, overflow: "hidden",
      border: "1px solid rgba(0,0,0,0.08)", background: "#fff",
      boxShadow: "0 8px 40px rgba(0,0,0,0.06)",
      display: "flex", height: 300,
    }}>
      {/* Chat side */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", borderRight: "1px solid rgba(0,0,0,0.06)" }}>
        <div style={{ padding: "10px 16px", borderBottom: "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%",
            background: isDataScientist ? "linear-gradient(135deg, rgba(16,185,129,0.4), rgba(59,130,246,0.3))" : "linear-gradient(135deg, rgba(180,140,60,0.4), rgba(120,180,80,0.3))",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700,
          }}>{isDataScientist ? "KT" : "EB"}</div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700 }}>{isDataScientist ? "Kenji Tanaka" : "Earl Briggs"}</div>
            <div style={{ fontSize: 9, color: "#111" }}>{isDataScientist ? "Former Head of DS, Rideshare Platform" : "4th Gen Farmer · Corn & Soybean · 40 yrs"}</div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{
              fontSize: 8, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
              background: isDataScientist ? "rgba(30,58,110,0.1)" : "rgba(180,140,60,0.1)",
              color: isDataScientist ? "#1e3a6e" : "#b45309",
            }}>{isDataScientist ? "NETWORK" : "INTERNAL"}</span>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#10b981" }} />
          </div>
        </div>
        <div style={{ flex: 1, overflow: "hidden", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
          {demoLines.slice(0, visibleLines).map((line, i) => (
            <div key={`${cycle}-${i}`} style={{
              display: "flex", gap: 6,
              flexDirection: line.type === "you" ? "row-reverse" : "row",
              animation: "fadeSlideIn 0.35s ease",
            }}>
              <div style={{
                maxWidth: "80%", padding: "7px 11px", borderRadius: 10,
                background: line.type === "you" ? "rgba(0,0,0,0.04)" : "#fff",
                border: line.type === "expert" ? "1px solid rgba(0,0,0,0.06)" : "none",
                fontSize: 11, lineHeight: 1.5, color: "#111",
              }}>
                {line.text}
              </div>
            </div>
          ))}
          {visibleLines < demoLines.length && visibleLines > 0 && (
            <div style={{ display: "flex", gap: 6, flexDirection: demoLines[visibleLines]?.type === "you" ? "row-reverse" : "row" }}>
              <div style={{ padding: "8px 12px", borderRadius: 10, background: "rgba(0,0,0,0.04)", display: "flex", gap: 3 }}>
                <div style={{ width: 4, height: 4, borderRadius: "50%", background: "rgba(0,0,0,0.25)", animation: "typingBounce 1.2s infinite" }} />
                <div style={{ width: 4, height: 4, borderRadius: "50%", background: "rgba(0,0,0,0.25)", animation: "typingBounce 1.2s infinite 0.2s" }} />
                <div style={{ width: 4, height: 4, borderRadius: "50%", background: "rgba(0,0,0,0.25)", animation: "typingBounce 1.2s infinite 0.4s" }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Extraction + data side */}
      <div style={{ width: 300, display: "flex", flexDirection: "column", background: "rgba(0,0,0,0.01)" }}>
        <div style={{ padding: "8px 12px", borderBottom: "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700 }}>Live Extraction</div>
            {signals.length > 0 && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 10, background: "rgba(99,102,241,0.1)", color: "#6366f1" }}>{signals.length}</span>}
          </div>
          {signals.length > 0 && <span style={{ fontSize: 7, padding: "2px 6px", borderRadius: 4, background: "rgba(16,185,129,0.1)", color: "#10b981", fontWeight: 600 }}>Data connected</span>}
        </div>
        <div style={{ flex: 1, overflow: "hidden", padding: "8px 10px", display: "flex", flexDirection: "column", gap: 6 }}>
          {signals.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#111", fontSize: 10 }}>Listening for signals...</div>
          )}
          {signals.map((s, i) => {
            if (!s) return null;
            const tc = tagColors[s.tag] || tagColors.variable;
            const m = s.match;
            const statusColors: Record<string, { color: string; bg: string }> = {
              direct: { color: "#10b981", bg: "rgba(16,185,129,0.08)" },
              proxy: { color: "#f59e0b", bg: "rgba(245,158,11,0.08)" },
              derived: { color: "#6366f1", bg: "rgba(99,102,241,0.08)" },
            };
            const sc = m ? (statusColors[m.status] || statusColors.direct) : statusColors.direct;
            return (
              <div key={`${cycle}-${i}`} style={{
                borderRadius: 8, border: "1px solid rgba(99,102,241,0.12)",
                background: "#fff", animation: "fadeSlideIn 0.4s ease", overflow: "hidden",
              }}>
                {/* Variable */}
                <div style={{ padding: "7px 10px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 3 }}>
                    <span style={{ fontSize: 7, fontWeight: 700, padding: "1px 4px", borderRadius: 3, textTransform: "uppercase", background: tc.bg, color: tc.color }}>{s.tag}</span>
                    <span style={{ fontSize: 9.5, fontWeight: 700 }}>{s.name.replace(/_/g, " ")}</span>
                  </div>
                  <div style={{ fontSize: 8.5, color: "#111", fontStyle: "italic", lineHeight: 1.4 }}>"{s.quote}"</div>
                </div>
                {/* Data match */}
                {m && (
                  <div style={{ padding: "6px 10px", borderTop: "1px solid rgba(0,0,0,0.04)", background: sc.bg }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 3 }}>
                      <span style={{ fontSize: 7, fontWeight: 700, padding: "1px 4px", borderRadius: 3, background: sc.bg, color: sc.color, textTransform: "uppercase" }}>{m.status}</span>
                      <span style={{ fontSize: 9, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", color: "#1e3a6e" }}>{m.col}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 8, fontFamily: "'IBM Plex Mono', monospace", color: "#111" }}>{m.pct} of rows</span>
                      {/* Strength bar */}
                      <div style={{ display: "flex", alignItems: "center", gap: 3, flex: 1 }}>
                        <div style={{ flex: 1, height: 3, background: "rgba(0,0,0,0.06)", borderRadius: 2, overflow: "hidden" }}>
                          <div style={{
                            height: "100%", borderRadius: 2,
                            width: m.strength === "Strong" ? "80%" : m.strength === "Moderate" ? "50%" : "25%",
                            background: m.strength === "Strong" ? "#10b981" : m.strength === "Moderate" ? "#f59e0b" : "#ef4444",
                          }} />
                        </div>
                        <span style={{ fontSize: 7, color: "#111" }}>{m.strength}</span>
                      </div>
                      <span style={{ fontSize: 8, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", color: parseFloat(m.corr) > 0 ? "#10b981" : "#ef4444" }}>{m.corr}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function LoginScreen({ onLogin }: Props) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const loginRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  useScrollReveal();

  const handleMouseMove = (e: React.MouseEvent) => {
    if (cursorRef.current) {
      cursorRef.current.style.left = `${e.clientX}px`;
      cursorRef.current.style.top = `${e.clientY}px`;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.toLowerCase() === "friends") {
      onLogin();
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  const scrollToLogin = () => {
    loginRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      onMouseMove={handleMouseMove}
      style={{
        minHeight: "100vh",
        background: "#fff",
        fontFamily: "'Inter', sans-serif",
        overflowY: "auto",
      }}>

      {/* ── Sticky top nav ── */}
      <nav style={{
        position: "sticky", top: 0, zIndex: 100,
        padding: "12px 32px",
        background: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src="/tacit-logo.png" alt="Tacit" style={{ height: 22, width: "auto" }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          <a href="#use-cases" onClick={e => { e.preventDefault(); document.getElementById("use-cases")?.scrollIntoView({ behavior: "smooth" }); }}
            style={{ fontSize: 14, fontWeight: 500, color: "#111", textDecoration: "none", cursor: "pointer" }}>Use Cases</a>
          <a href="#how-it-works" onClick={e => { e.preventDefault(); document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" }); }}
            style={{ fontSize: 14, fontWeight: 500, color: "#111", textDecoration: "none", cursor: "pointer" }}>How It Works</a>
          <a href="#faq" onClick={e => { e.preventDefault(); document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" }); }}
            style={{ fontSize: 14, fontWeight: 500, color: "#111", textDecoration: "none", cursor: "pointer" }}>FAQ</a>
          <button onClick={scrollToLogin} style={{
            padding: "7px 18px", borderRadius: 120, background: "#111", color: "#fff",
            border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>Log in</button>
        </div>
      </nav>
      <style>{`
        @keyframes loginShake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-5px); }
          80% { transform: translateX(5px); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeSlideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes typingBounce { 0%, 80%, 100% { transform: translateY(0); } 40% { transform: translateY(-3px); } }

        /* 1. Cycling hero text — Vercel-style gradient rotation */
        .hero-cycle-wrap {
          position: relative;
          display: inline-block;
          height: 1.3em;
          min-width: 520px;
          vertical-align: bottom;
          overflow: hidden;
          perspective: 600px;
        }
        .hero-cycle-word {
          display: block;
          position: absolute;
          top: 0; left: 0; right: 0;
          text-align: center;
          font-weight: 700;
          opacity: 0;
          transform-origin: center bottom;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .hero-cycle-1 {
          -webkit-text-fill-color: #111;
          animation: heroWord1 12s cubic-bezier(0.22, 1, 0.36, 1) infinite;
        }
        .hero-cycle-2 {
          -webkit-text-fill-color: #111;
          animation: heroWord2 12s cubic-bezier(0.22, 1, 0.36, 1) infinite;
        }
        .hero-cycle-3 {
          -webkit-text-fill-color: #111;
          animation: heroWord3 12s cubic-bezier(0.22, 1, 0.36, 1) infinite;
        }
        .hero-cycle-4 {
          -webkit-text-fill-color: #111;
          animation: heroWord4 12s cubic-bezier(0.22, 1, 0.36, 1) infinite;
        }
        @keyframes heroWord1 {
          0%      { opacity: 1; transform: rotateX(0deg) scale(1); filter: blur(0); }
          20%     { opacity: 1; transform: rotateX(0deg) scale(1); filter: blur(0); }
          24%     { opacity: 0; transform: rotateX(-90deg) scale(0.95); filter: blur(3px); }
          25%     { opacity: 0; }
          95%     { opacity: 0; transform: rotateX(90deg) scale(0.95); filter: blur(3px); }
          100%    { opacity: 1; transform: rotateX(0deg) scale(1); filter: blur(0); }
        }
        @keyframes heroWord2 {
          0%, 24% { opacity: 0; transform: rotateX(90deg) scale(0.95); filter: blur(3px); }
          28%     { opacity: 1; transform: rotateX(0deg) scale(1); filter: blur(0); }
          45%     { opacity: 1; transform: rotateX(0deg) scale(1); filter: blur(0); }
          49%     { opacity: 0; transform: rotateX(-90deg) scale(0.95); filter: blur(3px); }
          100%    { opacity: 0; }
        }
        @keyframes heroWord3 {
          0%, 49% { opacity: 0; transform: rotateX(90deg) scale(0.95); filter: blur(3px); }
          53%     { opacity: 1; transform: rotateX(0deg) scale(1); filter: blur(0); }
          70%     { opacity: 1; transform: rotateX(0deg) scale(1); filter: blur(0); }
          74%     { opacity: 0; transform: rotateX(-90deg) scale(0.95); filter: blur(3px); }
          100%    { opacity: 0; }
        }
        @keyframes heroWord4 {
          0%, 74% { opacity: 0; transform: rotateX(90deg) scale(0.95); filter: blur(3px); }
          78%     { opacity: 1; transform: rotateX(0deg) scale(1); filter: blur(0); }
          93%     { opacity: 1; transform: rotateX(0deg) scale(1); filter: blur(0); }
          97%     { opacity: 0; transform: rotateX(-90deg) scale(0.95); filter: blur(3px); }
          100%    { opacity: 0; }
        }

        @keyframes statCountUp {
          0% { opacity: 0; transform: rotateX(90deg) scale(0.8); filter: blur(4px); }
          60% { opacity: 1; transform: rotateX(-5deg) scale(1.02); filter: blur(0); }
          100% { opacity: 1; transform: rotateX(0deg) scale(1); filter: blur(0); }
        }

        @keyframes cursorBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }

        /* Static gradient fallback for other uses */
        .gradient-text {
          background: linear-gradient(135deg, #111 0%, #1e3a6e 50%, #3b7dd8 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* 2. Scroll-triggered fade-in */
        .scroll-reveal {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .scroll-reveal.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* 3. Glowing CTA */
        .glow-btn {
          box-shadow: 0 0 20px rgba(30,58,110,0.3), 0 0 60px rgba(59,125,216,0.15);
          animation: btnGlow 3s ease-in-out infinite;
        }
        @keyframes btnGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(30,58,110,0.3), 0 0 60px rgba(59,125,216,0.15); }
          50% { box-shadow: 0 0 30px rgba(30,58,110,0.5), 0 0 80px rgba(59,125,216,0.25); }
        }
      `}</style>

      {/* ── Hero ── */}
      <div style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 32px",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Morphing pixel grid + orbs */}
        <PixelGrid />
        <div className="ambient-bg" style={{ position: "absolute" }}>
          <div className="ambient-orb-3" />
          <div className="ambient-orb-4" />
        </div>
        <img
          src="/tacit-logo.png"
          alt="Tacit"
          style={{ height: 48, width: "auto", marginBottom: 32, animation: "fadeUp 0.6s ease-out" }}
        />

        <h1 style={{
          fontSize: 46,
          fontWeight: 700,
          fontFamily: "'Merriweather', serif",
          textAlign: "center",
          lineHeight: 1.3,
          margin: "0 0 16px",
          maxWidth: 700,
          animation: "fadeUp 0.6s ease-out 0.1s both",
          color: "#111",
        }}>
          <span style={{ fontWeight: 300, letterSpacing: "-0.01em" }}>Expert conversations into</span><br />
          <span className="hero-cycle-wrap">
            {["model-ready features.", "engineered variables.", "predictive signals.", "structured priors."].map((word, i) => (
              <span key={word} className={`hero-cycle-word hero-cycle-${i + 1}`} style={{ fontStyle: "italic", fontWeight: 700, fontFamily: "'Merriweather', serif" }}>{word}</span>
            ))}
          </span>
        </h1>

        <p style={{
          fontSize: 18,
          color: "#111",
          textAlign: "center",
          maxWidth: 680,
          lineHeight: 1.7,
          margin: "0 0 40px",
          fontWeight: 400,
          animation: "fadeUp 0.6s ease-out 0.2s both",
        }}>
          The features your model is missing live in someone's head. <em style={{ fontStyle: "italic", fontWeight: 700, fontFamily: "'Merriweather', serif", color: "#111" }}>We get them out.</em>
        </p>

        {/* Live demo animation */}
        <div style={{ marginBottom: 48, animation: "fadeUp 0.8s ease-out 0.3s both", width: "100%", display: "flex", justifyContent: "center" }}>
          <LiveDemo />
        </div>

        {/* 5-step summary with pixel grid background */}
        <div style={{
          position: "relative",
          display: "flex",
          gap: 24,
          marginBottom: 48,
          animation: "fadeUp 0.6s ease-out 0.5s both",
          flexWrap: "wrap",
          justifyContent: "center",
          maxWidth: 900,
          padding: "32px 20px",
          borderRadius: 20,
        }}>
          {/* Pixel grid behind the steps */}
          <div style={{ position: "absolute", inset: 0, borderRadius: 20, overflow: "hidden", zIndex: 0 }}>
            <PixelGrid />
            {/* Center fade so text is readable */}
            <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at center, rgba(255,255,255,0.85) 30%, rgba(255,255,255,0.4) 70%, transparent 100%)" }} />
          </div>
          {STEPS.map(s => (
            <div key={s.num} style={{ width: 150, textAlign: "center", position: "relative", zIndex: 1 }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "linear-gradient(135deg, #1e3a6e, #3b7dd8)", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 14, fontWeight: 700,
                margin: "0 auto 10px",
              }}>{s.num}</div>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{s.title}</div>
              <div style={{ fontSize: 13, color: "#111", lineHeight: 1.5 }}>{s.desc}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 12, animation: "fadeUp 0.6s ease-out 0.4s both" }}>
          <button
            className="glow-btn"
            onClick={scrollToLogin}
            style={{
              padding: "16px 36px", borderRadius: 120,
              background: "linear-gradient(135deg, #1e3a6e, #3b7dd8)", color: "#fff", border: "none",
              fontSize: 15, fontWeight: 600, cursor: "pointer",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; }}
          >
            Try the demo
          </button>
          <a
            href="#use-cases"
            onClick={e => {
              e.preventDefault();
              document.getElementById("use-cases")?.scrollIntoView({ behavior: "smooth" });
            }}
            style={{
              padding: "14px 32px", borderRadius: 120,
              background: "transparent", color: "#111",
              border: "1px solid rgba(0,0,0,0.15)",
              fontSize: 14, fontWeight: 600, cursor: "pointer",
              textDecoration: "none",
              display: "flex", alignItems: "center",
            }}
          >
            See use cases
          </a>
        </div>

      </div>

      {/* ── The problem ── */}
      <div className="scroll-reveal" style={{
        padding: "80px 32px",
        background: "rgba(0,0,0,0.02)",
        borderTop: "1px solid rgba(0,0,0,0.06)",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
      }}>
        <div style={{ maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#111", marginBottom: 16 }}>
            The problem
          </div>
          <h2 style={{ fontSize: 32, fontWeight: 300, fontFamily: "'Merriweather', serif", margin: "0 0 20px", lineHeight: 1.4 }}>
            The data was there.<br /><span style={{ color: "#1e3a6e" }}>The feature wasn't.</span>
          </h2>
          <p style={{ fontSize: 16, color: "#111", lineHeight: 1.8, margin: "0 0 32px" }}>
            A rideshare exec knows surge above 40% flips driver behavior. A farmer knows the crop is lost if the soil crumbles at planting. A fraud analyst knows which patterns precede chargebacks before the data catches up. <strong>This knowledge exists — but it lives in people's heads or conversations, and dies in transcripts nobody reads again. Organizations spend millions on data infrastructure and model development, yet the most valuable signal — domain expertise — never makes it into the pipeline.</strong>
          </p>
          <div style={{ display: "flex", gap: 16, justifyContent: "center", maxWidth: 720, width: "100%" }}>
            {[
              { num: "$15M+", label: "What large enterprises spend annually on data infrastructure and model development", color: "#dc2626", delay: "0s" },
              { num: "83%", label: "Of domain expertise never formalized into model features", color: "#b45309", delay: "0.15s" },
              { num: "$47B", label: "Annual cost of poor model decisions across US enterprises", color: "#1e3a6e", delay: "0.3s" },
            ].map((s: any, i: number) => (
              <div key={s.label} className="scroll-reveal" style={{
                flex: 1, padding: "22px 18px", textAlign: "center",
                background: "rgba(255,255,255,0.6)",
                backdropFilter: "blur(16px) saturate(1.4)",
                WebkitBackdropFilter: "blur(16px) saturate(1.4)",
                borderRadius: 14,
                border: "1px solid rgba(255,255,255,0.5)",
                boxShadow: "0 4px 24px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.8)",
              }}>
                <div className="stat-flip" style={{
                  fontSize: 32, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace",
                  color: s.color, marginBottom: 6, lineHeight: 1,
                  animation: `statCountUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${s.delay} both`,
                }}>{s.num}</div>
                <div style={{ fontSize: 11, color: "#111", lineHeight: 1.5 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Two modes ── */}
      <div className="scroll-reveal" style={{ padding: "60px 32px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#111", marginBottom: 12 }}>Two ways to use Tacit</div>
            <h2 style={{ fontSize: 32, fontWeight: 300, fontFamily: "'Merriweather', serif", margin: 0 }}>Our experts or yours</h2>
          </div>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            {/* We find the expert — FIRST */}
            <div style={{ flex: "1 1 340px", padding: "28px 28px", borderRadius: 16, border: "1.5px solid rgba(30,58,110,0.15)", background: "rgba(30,58,110,0.02)" }}>
              <div style={{ fontSize: 24, marginBottom: 12 }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1e3a6e" strokeWidth="1.5" strokeLinecap="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></svg>
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6, color: "#111" }}>We find the expert</div>
              <p style={{ fontSize: 14, color: "#111", lineHeight: 1.7, margin: "0 0 14px" }}>
                Share your data question or hypothesis and upload your dataset schema. We match you with someone from our network who's built that exact model before — matched by feature overlap, not keywords.
              </p>
              <div style={{
                padding: "14px 16px", borderRadius: 10, background: "#fff", border: "1px solid rgba(30,58,110,0.1)", marginBottom: 14,
                fontSize: 13, color: "#111", lineHeight: 1.6,
              }}>
                <div style={{ fontStyle: "italic", marginBottom: 6 }}>"Above 40% surge exposure, drivers start gaming the system. They go offline to trigger surges then come back. Net effect on supply is negative."</div>
                <div style={{ fontSize: 11, color: "#1e3a6e", fontWeight: 500 }}>→ Extracted: <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700 }}>surge_fatigue_gt_40 (binary) — 82% certainty</span></div>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {["Schema-matched experts", "Calibration scores", "Variable library"].map(t => (
                  <span key={t} style={{ fontSize: 9, padding: "3px 10px", borderRadius: 20, background: "rgba(30,58,110,0.08)", color: "#1e3a6e", fontWeight: 600 }}>{t}</span>
                ))}
              </div>
            </div>
            {/* Bring your own expert — SECOND */}
            <div style={{ flex: "1 1 340px", padding: "28px 28px", borderRadius: 16, border: "1px solid rgba(0,0,0,0.08)", background: "#fff" }}>
              <div style={{ fontSize: 24, marginBottom: 12 }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.5" strokeLinecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6, color: "#111" }}>Bring your own expert</div>
              <p style={{ fontSize: 14, color: "#111", lineHeight: 1.7, margin: "0 0 14px" }}>
                Interview your internal domain specialists — the farmer who knows the soil, the claims manager who knows fraud patterns. They speak naturally. Tacit translates.
              </p>
              <div style={{
                padding: "14px 16px", borderRadius: 10, background: "rgba(0,0,0,0.02)", border: "1px solid rgba(0,0,0,0.06)", marginBottom: 14,
                fontSize: 13, color: "#111", lineHeight: 1.6,
              }}>
                <div style={{ fontStyle: "italic", marginBottom: 6 }}>"You grab a handful of dirt and squeeze it. If it crumbles instead of holding together, you're starting behind. Doesn't matter how much rain you get later."</div>
                <div style={{ fontSize: 11, color: "#111", fontWeight: 500 }}>→ Tacit extracts: <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, color: "#1e3a6e" }}>apr_moisture_lt_40 (binary) — 92% certainty</span></div>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {["Non-technical experts", "Natural language in", "Structured features out"].map(t => (
                  <span key={t} style={{ fontSize: 9, padding: "3px 10px", borderRadius: 20, background: "rgba(0,0,0,0.05)", color: "#111", fontWeight: 600 }}>{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Human in the Loop diagram ── */}
      <div className="scroll-reveal" style={{ padding: "80px 32px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#111", marginBottom: 12 }}>Human-in-the-Loop</div>
            <h2 style={{ fontSize: 32, fontWeight: 300, fontFamily: "'Merriweather', serif", margin: 0, lineHeight: 1.4 }}>
              The expert knows. The data confirms.<br /><span style={{ color: "#1e3a6e" }}>You ask the right questions.</span>
            </h2>
          </div>

          {/* Animated flow diagram */}
          <div style={{ position: "relative", padding: "20px 0" }}>
            <svg viewBox="0 0 820 380" style={{ width: "100%", height: "auto", overflow: "visible" }}>

              {/* Arrow paths — rounded corners at bends, arrows stop at box edge */}
              {/* Expert right → Tacit left */}
              <path d="M 155 95 L 245 95" fill="none" stroke="#111" strokeWidth="1.5" />
              <polygon points="240,90 250,95 240,100" fill="#111" />
              {/* Tacit right → Data left */}
              <path d="M 400 95 L 490 95" fill="none" stroke="#111" strokeWidth="1.5" />
              <polygon points="485,90 495,95 485,100" fill="#111" />
              {/* Data bottom-center → rounded bend → You right-center */}
              <path d="M 565 130 L 565 265 Q 565 285 545 285 L 440 285" fill="none" stroke="#111" strokeWidth="1.5" />
              <polygon points="445,280 435,285 445,290" fill="#111" />
              {/* You left-center → rounded bend → Expert bottom-center */}
              <path d="M 190 285 L 105 285 Q 85 285 85 265 L 85 130" fill="none" stroke="#111" strokeWidth="1.5" />
              <polygon points="80,135 85,125 90,135" fill="#111" />

              {/* Traveling dots */}
              <circle r="5" fill="#1e3a6e">
                <animateMotion dur="2s" repeatCount="indefinite" path="M 155 95 L 245 95" />
              </circle>
              <circle r="5" fill="#3b7dd8">
                <animateMotion dur="2s" repeatCount="indefinite" path="M 400 95 L 490 95" begin="0.3s" />
              </circle>
              <circle r="5" fill="#059669">
                <animateMotion dur="3s" repeatCount="indefinite" path="M 565 130 L 565 265 Q 565 285 545 285 L 440 285" begin="0.6s" />
              </circle>
              <circle r="5" fill="#8b5cf6">
                <animateMotion dur="3s" repeatCount="indefinite" path="M 190 285 L 105 285 Q 85 285 85 265 L 85 130" begin="1s" />
              </circle>

              {/* Arrow labels */}
              <text x="200" y="85" textAnchor="middle" fontSize="10" fill="#111" fontWeight="600">knowledge</text>
              <text x="445" y="85" textAnchor="middle" fontSize="10" fill="#111" fontWeight="600">variables</text>
              <text x="590" y="210" textAnchor="middle" fontSize="10" fill="#111" fontWeight="600">live stats</text>
              <text x="55" y="210" textAnchor="middle" fontSize="10" fill="#111" fontWeight="600">follow-ups</text>

              {/* Boxes on top */}
              <rect x="15" y="60" width="140" height="70" rx="12" fill="white" stroke="#111" strokeWidth="1.5" />
              <text x="85" y="90" textAnchor="middle" fontSize="14" fontWeight="700" fill="#111">Expert</text>
              <text x="85" y="110" textAnchor="middle" fontSize="10" fill="#111">speaks naturally</text>

              <rect x="250" y="60" width="150" height="70" rx="12" fill="rgba(30,58,110,0.04)" stroke="#111" strokeWidth="1.5" />
              <image href="/tacit-logo.png" x="280" y="72" width="90" height="22" />
              <text x="325" y="110" textAnchor="middle" fontSize="10" fill="#111">extracts variables</text>

              <rect x="495" y="60" width="140" height="70" rx="12" fill="white" stroke="#111" strokeWidth="1.5" />
              <text x="565" y="90" textAnchor="middle" fontSize="14" fontWeight="700" fill="#111">Your Data</text>
              <text x="565" y="110" textAnchor="middle" fontSize="10" fill="#111">matched live</text>

              {/* Match type buckets — right of Your Data, spaced out */}
              <path d="M 635 72 Q 665 52 695 52" fill="none" stroke="#10b981" strokeWidth="1.5" />
              <circle cx="695" cy="52" r="2.5" fill="#10b981" />
              <path d="M 635 85 Q 665 82 695 82" fill="none" stroke="#f59e0b" strokeWidth="1.5" />
              <circle cx="695" cy="82" r="2.5" fill="#f59e0b" />
              <path d="M 635 100 Q 665 112 695 112" fill="none" stroke="#6366f1" strokeWidth="1.5" />
              <circle cx="695" cy="112" r="2.5" fill="#6366f1" />
              <path d="M 635 115 Q 665 142 695 142" fill="none" stroke="#ef4444" strokeWidth="1.5" />
              <circle cx="695" cy="142" r="2.5" fill="#ef4444" />

              <rect x="700" y="40" width="100" height="24" rx="7" fill="rgba(16,185,129,0.08)" stroke="#10b981" strokeWidth="1" />
              <text x="750" y="57" textAnchor="middle" fontSize="10" fontWeight="700" fill="#10b981">Direct</text>

              <rect x="700" y="70" width="100" height="24" rx="7" fill="rgba(245,158,11,0.08)" stroke="#f59e0b" strokeWidth="1" />
              <text x="750" y="87" textAnchor="middle" fontSize="10" fontWeight="700" fill="#f59e0b">Proxy</text>

              <rect x="700" y="100" width="100" height="24" rx="7" fill="rgba(99,102,241,0.08)" stroke="#6366f1" strokeWidth="1" />
              <text x="750" y="117" textAnchor="middle" fontSize="10" fontWeight="700" fill="#6366f1">Derived</text>

              <rect x="700" y="130" width="100" height="24" rx="7" fill="rgba(239,68,68,0.08)" stroke="#ef4444" strokeWidth="1" />
              <text x="750" y="147" textAnchor="middle" fontSize="10" fontWeight="700" fill="#ef4444">Missing</text>

              {/* You box */}
              <rect x="190" y="250" width="250" height="70" rx="12" fill="rgba(139,92,246,0.04)" stroke="#111" strokeWidth="1.5" />
              <text x="315" y="275" textAnchor="middle" fontSize="14" fontWeight="700" fill="#111">You</text>
              <text x="315" y="293" textAnchor="middle" fontSize="10" fill="#111">see how it matches your data live,</text>
              <text x="315" y="307" textAnchor="middle" fontSize="10" fill="#111">ask deeper questions</text>

            </svg>

            {/* Bouncy pop-up thumbnails */}
            <style>{`
              @keyframes popIn { 0% { opacity:0; transform:scale(0.5) translateY(10px); } 100% { opacity:1; transform:scale(1) translateY(0); } }
              @keyframes floatBounce { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-4px); } }
              .hitl-popup { opacity: 0; }
              .scroll-reveal.visible .hitl-popup { animation-play-state: running; }
            `}</style>

            {/* Pop-up 1: Expert — chat bubble */}
            <div className="hitl-popup" style={{
              position: "absolute", top: "2%", left: "2%", zIndex: 5,
              padding: "10px 14px", borderRadius: 10, background: "#fff", border: "1.5px solid rgba(0,0,0,0.1)",
              boxShadow: "0 6px 24px rgba(0,0,0,0.1)", fontSize: 11, color: "#111", maxWidth: 170,
              animation: "popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s both, floatBounce 3s ease infinite 1s",
            }}>
              <div style={{ fontStyle: "italic", lineHeight: 1.5, color: "rgba(0,0,0,0.7)" }}>"Above 40% surge, driver behavior shifts"</div>
              <div style={{ marginTop: 4, fontSize: 9, color: "#1e3a6e", fontWeight: 700 }}>— Expert</div>
            </div>

            {/* Pop-up 2: Tacit — extracted variable card */}
            <div className="hitl-popup" style={{
              position: "absolute", top: "2%", left: "38%", zIndex: 5,
              padding: "10px 14px", borderRadius: 10, background: "#fff", border: "1.5px solid rgba(99,102,241,0.2)",
              boxShadow: "0 6px 24px rgba(0,0,0,0.1)", fontSize: 11, maxWidth: 160,
              animation: "popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.6s both, floatBounce 3s ease infinite 1.3s",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
                <span style={{ fontSize: 8, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: "rgba(245,158,11,0.1)", color: "#d97706" }}>THRESHOLD</span>
              </div>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, fontSize: 12, color: "#111" }}>surge_gt_40</div>
              <div style={{ fontSize: 10, color: "#111", marginTop: 2 }}>Certainty 82%</div>
            </div>

            {/* Pop-up 3: Data — mini table */}
            <div className="hitl-popup" style={{
              position: "absolute", top: "2%", left: "58%", zIndex: 5,
              padding: "10px 12px", borderRadius: 10, background: "#fff", border: "1.5px solid rgba(0,0,0,0.1)",
              boxShadow: "0 6px 24px rgba(0,0,0,0.1)", fontSize: 10, maxWidth: 155,
              animation: "popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.9s both, floatBounce 3s ease infinite 1.6s",
            }}>
              <div style={{ fontWeight: 700, marginBottom: 4, color: "#059669", fontSize: 8, textTransform: "uppercase" }}>Column match</div>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, fontWeight: 700, color: "#111" }}>Surge_Session_Pct</div>
              <div style={{ display: "flex", gap: 8, marginTop: 4, fontSize: 10 }}>
                <span style={{ color: "#111" }}>22% of rows</span>
                <span style={{ color: "#10b981", fontWeight: 700 }}>+0.19</span>
              </div>
            </div>

            {/* Pop-up 4: You — suggested question (overlaps You box top-right) */}
            <div className="hitl-popup" style={{
              position: "absolute", bottom: "3%", right: "22%", zIndex: 5,
              padding: "10px 14px", borderRadius: 10, background: "rgba(30,58,110,0.04)", border: "1.5px solid rgba(30,58,110,0.15)",
              boxShadow: "0 6px 24px rgba(0,0,0,0.1)", fontSize: 11, maxWidth: 200, color: "#1e3a6e",
              animation: "popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 1.2s both, floatBounce 3s ease infinite 1.9s",
            }}>
              <div style={{ fontWeight: 700, marginBottom: 3, fontSize: 9 }}>💡 Ask:</div>
              <div style={{ lineHeight: 1.5 }}>"Only 22% above 40%. Should we lower the threshold?"</div>
            </div>
          </div>

          <div style={{ textAlign: "center", fontSize: 14, color: "#111", lineHeight: 1.7, maxWidth: 520, margin: "0 auto" }}>
            The expert talks. Tacit extracts. Your data validates in real time. <strong>You see what matches, what's missing, and what to ask next — all before the conversation ends.</strong>
          </div>
        </div>
      </div>

      {/* ── Key insight callout ── */}
      <div className="scroll-reveal" style={{ padding: "60px 32px", textAlign: "center" }}>
        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          <div style={{
            fontSize: 28, fontWeight: 300, fontFamily: "'Merriweather', serif",
            lineHeight: 1.6, color: "#111", fontStyle: "italic",
          }}>
            Kenji speaks in features and thresholds.<br />
            Earl speaks in soil and seasons.<br />
            <strong style={{ fontWeight: 700, color: "#1e3a6e" }}>Tacit handles both.</strong>
          </div>
          <div style={{ fontSize: 16, color: "#111", marginTop: 20, lineHeight: 1.7 }}>
            If the expert could structure their own knowledge, they wouldn't need us.<br />
            The extraction is harder with Earl — and <strong>that's exactly why Tacit exists.</strong>
          </div>
        </div>
      </div>

      {/* ── Use cases ── */}
      <div id="use-cases" className="scroll-reveal" style={{ padding: "80px 0" }}>
        <div style={{ textAlign: "center", marginBottom: 60, padding: "0 32px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#111", marginBottom: 16 }}>
            Use cases
          </div>
          <h2 style={{ fontSize: 32, fontWeight: 300, fontFamily: "'Merriweather', serif", margin: 0, lineHeight: 1.4 }}>
            The expert knows. The model doesn't. <strong>We bridge the gap.</strong>
          </h2>
        </div>

        {USE_CASES.map((uc, i) => {
          const isReversed = i % 2 === 1;
          return (
            <div key={i} className="scroll-reveal" style={{
              padding: "60px 32px",
              background: i % 2 === 1 ? "rgba(0,0,0,0.02)" : "transparent",
              borderTop: i > 0 ? "1px solid rgba(0,0,0,0.04)" : "none",
            }}>
              <div style={{
                maxWidth: 960, margin: "0 auto",
                display: "flex", gap: 48, alignItems: "flex-start",
                flexDirection: isReversed ? "row-reverse" : "row",
                flexWrap: "wrap",
              }}>
                {/* Text side */}
                <div style={{ flex: "1 1 420px" }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 4,
                    background: uc.tagColor + "14", color: uc.tagColor,
                    textTransform: "uppercase", letterSpacing: "0.06em",
                  }}>{uc.tag}</span>
                  <h3 style={{ fontSize: 24, fontWeight: 700, margin: "14px 0 12px", lineHeight: 1.3, fontFamily: "'Merriweather', serif" }}>{uc.title}</h3>

                  <p style={{ fontSize: 15, color: "#111", lineHeight: 1.7, margin: "0 0 16px" }}>
                    {uc.scenario}
                  </p>

                  <p style={{ fontSize: 13, color: "#111", lineHeight: 1.6, margin: "0 0 16px", fontStyle: "italic" }}>
                    {uc.expert}
                  </p>

                  {/* Expert quote */}
                  <div style={{
                    padding: "16px 20px", borderRadius: 12,
                    background: "#fff",
                    border: "1px solid rgba(0,0,0,0.06)",
                    borderLeft: `3px solid ${uc.tagColor}`,
                    marginBottom: 16,
                  }}>
                    <div style={{ fontSize: 15, fontStyle: "italic", color: "#111", lineHeight: 1.7 }}>
                      "{uc.quote}"
                    </div>
                    <div style={{ fontSize: 11, color: "#111", marginTop: 8 }}>— {uc.speaker}</div>
                  </div>

                  <p style={{ fontSize: 14, color: "#111", lineHeight: 1.7, margin: 0 }}>
                    {uc.result}
                  </p>
                </div>

                {/* Right side — extracted variables + metric */}
                <div style={{ flex: "0 0 300px" }}>
                  {/* Metric callout */}
                  <div style={{
                    padding: "20px 24px", borderRadius: 14, marginBottom: 16,
                    background: uc.tagColor + "08", border: `1px solid ${uc.tagColor}18`,
                    textAlign: "center",
                  }}>
                    <div style={{ fontSize: 32, fontWeight: 300, fontFamily: "'IBM Plex Mono', monospace", color: uc.tagColor }}>
                      {uc.metric}
                    </div>
                    <div style={{ fontSize: 11, color: uc.tagColor, fontWeight: 500, marginTop: 4 }}>
                      {uc.metricLabel}
                    </div>
                  </div>

                  {/* Extracted variables */}
                  <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#111", marginBottom: 10 }}>
                    Variables extracted
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {uc.extracted.map((v, j) => (
                      <div key={j} style={{
                        padding: "8px 12px", borderRadius: 8,
                        background: "#fff",
                        border: "1px solid rgba(0,0,0,0.06)",
                        fontSize: 11, fontFamily: "'IBM Plex Mono', monospace",
                        color: "#111", lineHeight: 1.4,
                      }}>
                        {v}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── How it works ── */}
      <div id="how-it-works" className="scroll-reveal" style={{
        padding: "80px 32px",
        background: "rgba(0,0,0,0.02)",
        borderTop: "1px solid rgba(0,0,0,0.06)",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
      }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#111", marginBottom: 16 }}>
            How it works
          </div>
          <h2 style={{ fontSize: 32, fontWeight: 300, fontFamily: "'Merriweather', serif", margin: "0 0 48px" }}>
            Expert knowledge in. Enriched dataset out.
          </h2>

          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            {[
              {
                step: "1", title: "You describe the problem",
                desc: "\"We're predicting driver churn. We have surge data, ratings, and onboarding metrics but the model plateaued at 0.74 AUC.\"",
                detail: "Tacit analyzes your schema and matches you with a former Head of DS at a rideshare platform who built this exact model.",
              },
              {
                step: "2", title: "Your data connects live",
                desc: "Link Snowflake, BigQuery, Postgres — or drop an Excel file. Tacit scans every column: types, ranges, distributions.",
                detail: "23 columns detected. Field descriptions auto-read. Data stays connected for the entire session.",
              },
              {
                step: "3", title: "The expert talks. Tacit listens.",
                desc: "\"Above 40% surge exposure, drivers game the system.\" Tacit instantly matches this to your Surge_Session_Pct column — 22% of rows affected, correlation +0.19.",
                detail: "You see the match live and ask: \"Only 22% — should we lower the threshold?\" The expert adjusts. The data informed the question.",
              },
              {
                step: "4", title: "You review. We generate code.",
                desc: "4 direct matches, 1 derived from two columns, 1 missing. You accept or skip each one. Tacit writes the Python or SQL.",
                detail: "df['surge_gt_40'] = (df['Surge_Session_Pct'] > 40).astype(int) — or the equivalent ALTER TABLE in Snowflake SQL.",
              },
              {
                step: "5", title: "New columns. Your table.",
                desc: "6 engineered features pushed to your live database. A feature card documents every addition — what, why, and the exact quote.",
                detail: "Your model runs on richer data tomorrow. Every new column has a name, a source, and an audit trail.",
              },
            ].map(s => (
              <div key={s.step} style={{
                flex: "1 1 150px", maxWidth: 180, padding: "20px 16px", borderRadius: 16,
                background: "#fff", border: "1px solid rgba(0,0,0,0.06)",
                borderTop: "3px solid #111",
                textAlign: "left",
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: "linear-gradient(135deg, #1e3a6e, #3b7dd8)", color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 13, fontWeight: 700, marginBottom: 14,
                }}>{s.step}</div>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{s.title}</div>
                <div style={{ fontSize: 13, color: "#111", lineHeight: 1.5, marginBottom: 10 }}>{s.desc}</div>
                <div style={{
                  fontSize: 12, fontFamily: "'IBM Plex Mono', monospace",
                  color: "#111", lineHeight: 1.5,
                  padding: "8px 10px", borderRadius: 6, background: "rgba(0,0,0,0.02)",
                }}>{s.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Visual demos of key steps ── */}
      <div className="scroll-reveal" style={{ padding: "60px 32px 80px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", flexDirection: "column", gap: 48 }}>

          {/* Demo 1: Data Scan — table with match highlights */}
          <div className="scroll-reveal" style={{ display: "flex", gap: 32, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 300px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#059669", marginBottom: 8 }}>Scan your data</div>
              <h3 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 10px", fontFamily: "'Merriweather', serif" }}>We find where expert signals live in your dataset</h3>
              <p style={{ fontSize: 14, color: "#111", lineHeight: 1.7, margin: 0 }}>
                Expert says "humidity above 65% matters." We scan your columns and find <code style={{ fontSize: 12, background: "rgba(0,0,0,0.05)", padding: "1px 4px", borderRadius: 3 }}>Avg_Humidity</code> — a direct match. Proxy columns and latent variables are identified too.
              </p>
            </div>
            <div style={{ flex: "1 1 400px", borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", overflow: "hidden", background: "#fff" }}>
              <div style={{ padding: "8px 14px", background: "rgba(0,0,0,0.03)", fontSize: 9, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", justifyContent: "space-between" }}>
                <span>Your dataset</span><span style={{ color: "rgba(30,58,110,0.6)" }}>3 matches found</span>
              </div>
              {[
                { col: "Avg_Daily_Foot_Traffic", type: "float", match: false },
                { col: "Avg_Temperature", type: "float", match: false },
                { col: "Avg_Humidity", type: "float", match: true },
                { col: "Competitor_Count", type: "int", match: true },
                { col: "Avg_Employee_Tenure_Mo", type: "float", match: true },
                { col: "N_Flavors", type: "int", match: false },
                { col: "Review_Score", type: "float", match: false },
              ].map((row, i) => (
                <div key={i} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "6px 14px", borderTop: "1px solid rgba(0,0,0,0.04)",
                  background: row.match ? "rgba(30,58,110,0.04)" : "transparent",
                }}>
                  <span style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", fontWeight: row.match ? 700 : 400, color: row.match ? "#1e3a6e" : "rgba(0,0,0,0.5)" }}>
                    {row.col}
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 9, color: "#111", fontFamily: "'IBM Plex Mono', monospace" }}>{row.type}</span>
                    {row.match && <span style={{ fontSize: 7, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: "rgba(30,58,110,0.15)", color: "#1e3a6e" }}>MATCH</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Demo 2: Feature Engineering — transformation */}
          <div className="scroll-reveal" style={{ display: "flex", gap: 32, alignItems: "center", flexWrap: "wrap", flexDirection: "row-reverse" }}>
            <div style={{ flex: "1 1 300px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#b45309", marginBottom: 8 }}>Engineer features</div>
              <h3 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 10px", fontFamily: "'Merriweather', serif" }}>Expert thresholds become model-ready columns</h3>
              <p style={{ fontSize: 14, color: "#111", lineHeight: 1.7, margin: 0 }}>
                The expert said "above 65%." We create <code style={{ fontSize: 12, background: "rgba(0,0,0,0.05)", padding: "1px 4px", borderRadius: 3 }}>humidity_gt_65</code> as a binary feature. Your 10 columns become 16. No code required.
              </p>
            </div>
            <div style={{ flex: "1 1 400px", borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", overflow: "hidden", background: "#fff" }}>
              <div style={{ padding: "8px 14px", background: "rgba(0,0,0,0.03)", fontSize: 9, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: "0.06em" }}>Enriched dataset preview</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr>
                    <th style={{ padding: "6px 12px", textAlign: "left", fontSize: 9, color: "#111", fontWeight: 600 }}>Avg_Humidity</th>
                    <th style={{ padding: "6px 12px", textAlign: "left", fontSize: 9, color: "#1e3a6e", fontWeight: 700, background: "rgba(30,58,110,0.04)" }}>humidity_gt_65</th>
                    <th style={{ padding: "6px 12px", textAlign: "left", fontSize: 9, color: "#111", fontWeight: 600 }}>Competitor_Count</th>
                    <th style={{ padding: "6px 12px", textAlign: "left", fontSize: 9, color: "#1e3a6e", fontWeight: 700, background: "rgba(30,58,110,0.04)" }}>competitor_gt_3</th>
                  </tr>
                </thead>
                <tbody>
                  {[[58.4, 0, 2, 0], [71.2, 1, 4, 1], [43.1, 0, 1, 0], [82.5, 1, 3, 0], [67.8, 1, 5, 1]].map((row, i) => (
                    <tr key={i} style={{ borderTop: "1px solid rgba(0,0,0,0.04)" }}>
                      <td style={{ padding: "5px 12px", fontFamily: "'IBM Plex Mono', monospace", color: "#111" }}>{row[0]}</td>
                      <td style={{ padding: "5px 12px", fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, color: row[1] ? "#1e3a6e" : "rgba(0,0,0,0.15)", background: "rgba(30,58,110,0.02)" }}>{row[1]}</td>
                      <td style={{ padding: "5px 12px", fontFamily: "'IBM Plex Mono', monospace", color: "#111" }}>{row[2]}</td>
                      <td style={{ padding: "5px 12px", fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, color: row[3] ? "#1e3a6e" : "rgba(0,0,0,0.15)", background: "rgba(30,58,110,0.02)" }}>{row[3]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Demo 3: Push + Feature Card */}
          <div className="scroll-reveal" style={{ display: "flex", gap: 32, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 300px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#1e3a6e", marginBottom: 8 }}>Push + Document</div>
              <h3 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 10px", fontFamily: "'Merriweather', serif" }}>New columns. Your table. Full audit trail.</h3>
              <p style={{ fontSize: 14, color: "#111", lineHeight: 1.7, margin: 0 }}>
                Engineered features pushed directly to your database or exported as an enriched file. Every new column comes with a feature card — documenting what was added, the transformation applied, and the exact expert quote it came from.
              </p>
            </div>
            <div style={{ flex: "1 1 400px", borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", overflow: "hidden", background: "#fff" }}>
              <div style={{ padding: "10px 16px", background: "rgba(0,0,0,0.03)", fontSize: 9, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: "0.06em" }}>Feature card</div>
              {[
                { name: "surge_fatigue_gt_40", type: "Direct", color: "#10b981", source: "Surge_Session_Pct", code: "> 40", quote: "Above 40% surge, driver behavior shifts" },
                { name: "first_week_lt_8", type: "Direct", color: "#10b981", source: "First_Week_Rides", code: "< 8", quote: "Eight rides in week one — that's your activation number" },
                { name: "rating_drop_gt_15", type: "Derived", color: "#6366f1", source: "current_rating - rating_30d", code: "< -0.15", quote: "Look at the slope, not the number" },
                { name: "push_notif_fail", type: "Missing", color: "#ef4444", source: "—", code: "—", quote: "The silent killer — lives in infra logs" },
              ].map((f, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", borderTop: "1px solid rgba(0,0,0,0.04)" }}>
                  <span style={{ fontSize: 8, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: f.color + "15", color: f.color }}>{f.type}</span>
                  <span style={{ fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, color: "#111", width: 140 }}>{f.name}</span>
                  <span style={{ fontSize: 9, color: "#111", fontStyle: "italic", flex: 1 }}>"{f.quote}"</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── FAQ ── */}
      <div id="faq" className="scroll-reveal" style={{ padding: "80px 32px" }}>
        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#111", marginBottom: 16 }}>FAQ</div>
            <h2 style={{ fontSize: 32, fontWeight: 300, fontFamily: "'Merriweather', serif", margin: 0 }}>Common questions</h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {[
              {
                q: "What data does Tacit see?",
                a: "Tacit connects to your dataset — via upload or database connection — to compute summary statistics, distributions, and correlations. This is how we match expert variables to your columns in real time and run feature transformations. The expert never sees your data. Tacit needs it to do the matching and engineering."
              },
              {
                q: "Can experts share this kind of knowledge legally?",
                a: "Yes. Domain expertise — 'humidity above 65% drives sales' or 'surge above 40% causes gaming' — is professional knowledge shared freely at conferences, in publications, and on thousands of expert network calls daily. Experts share domain judgment, not employer-specific confidential data or material non-public information. This is the same type of knowledge exchanged on GLG and AlphaSights calls every day."
              },
              {
                q: "How is this different from a GLG or Tegus call?",
                a: "GLG gives you a 60-minute call and a PDF transcript. You extract nothing structured from it. Tacit is purpose-built for data and quantitative questions — we extract variables in real time, match them against your connected data during the conversation, and output transformation code in Python or SQL. The expert's knowledge becomes structured, reusable features pushed to your live table."
              },
              {
                q: "Does the expert see our data?",
                a: "No. The expert speaks naturally about their domain knowledge. Tacit scans your connected data in the background to find matches and suggest follow-up questions — but the expert never sees your dataset or column names. They will know the general problem you're working on (e.g. 'driver churn model') but not your specific data."
              },
              {
                q: "What happens to the expert's knowledge after the session?",
                a: "Every extracted variable is documented in a feature card with full attribution — the expert's name, the exact quote, certainty level, and the transformation applied. Variables go into the Tacit library so future clients working on similar problems can benefit from validated features."
              },
              {
                q: "What about NDA and compliance?",
                a: "Every interview is recorded with consent. Every extracted signal is traced to a source quote. The full audit trail — feature card, variable provenance, expert attribution — is exportable for compliance review. We support NDA agreements between clients and experts."
              },
            ].map((faq, i) => (
              <div key={i} style={{ padding: "20px 24px", borderRadius: 12, border: "1px solid rgba(0,0,0,0.06)", background: "#fff" }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>{faq.q}</div>
                <div style={{ fontSize: 13, color: "#111", lineHeight: 1.7 }}>{faq.a}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA / Login ── */}
      <div ref={loginRef} className="scroll-reveal" style={{
        padding: "80px 32px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#111", marginBottom: 16 }}>
          Try it
        </div>
        <h2 style={{ fontSize: 32, fontWeight: 300, fontFamily: "'Merriweather', serif", margin: "0 0 8px", textAlign: "center" }}>
          See it work.
        </h2>
        <p style={{ fontSize: 14, color: "#111", marginBottom: 32, textAlign: "center" }}>
          Watch a pre-built demo or explore the full platform.
        </p>

        <form onSubmit={handleSubmit} style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
          animation: shake ? "loginShake 0.4s ease" : "none",
        }}>
          <input
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(false); }}
            placeholder="Password"
            autoFocus={false}
            style={{
              width: 280,
              padding: "14px 24px",
              fontSize: 14,
              fontFamily: "'Inter', sans-serif",
              letterSpacing: "0.05em",
              border: `1px solid ${error ? "rgba(200,0,0,0.3)" : "rgba(0,0,0,0.12)"}`,
              borderRadius: 120,
              outline: "none",
              textAlign: "center",
              color: "#111",
              background: error ? "rgba(200,0,0,0.02)" : "#fff",
              transition: "border-color 0.2s, background 0.2s",
            }}
          />
          <button
            type="submit"
            style={{
              width: 280,
              padding: "13px 24px",
              fontSize: 14,
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600,
              letterSpacing: "0.02em",
              background: "linear-gradient(135deg, #1e3a6e, #3b7dd8)",
              color: "#fff",
              border: "none",
              borderRadius: 120,
              cursor: "pointer",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
          >
            Enter
          </button>
        </form>

        {error && (
          <p style={{ marginTop: 14, fontSize: 12, color: "rgba(180,0,0,0.6)" }}>
            Incorrect password
          </p>
        )}

        <div style={{ marginTop: 60, paddingTop: 32, borderTop: "1px solid rgba(0,0,0,0.06)", textAlign: "center", width: "100%", maxWidth: 500 }}>
          <div style={{ fontSize: 11, color: "#111" }}>
            Built in Berkeley.
          </div>
        </div>
      </div>
    </div>
  );
}
